import { OperativePortfolioResponseModel } from './../response/models/portfolio.model';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, Observer } from 'rxjs';
import { map } from 'rxjs/operators';
import { OperativeEnum } from './../../../services/api-names';
import { Utils } from './../../../utils/utils';
import { Serializer } from './../../../helpers/serializer/seriealizer';
import { SessionService } from './../../../services/session.service';
import {
  TechLibPortalConfig,
  TECH_LIB_PORTAL_CONFIG,
} from './../../../tech-lib-portal.interceptor';
import { OperativePortfolioResponseInterface } from '../response/interfaces/portfolio.interface';
import { OperativeModelPortfolioResponseInterface } from '../response/interfaces/modelportfolio.interface';
import { OperativeModelPortfolioResponseModel } from '../response/models/modelportfolio.model';
import { OperativeBankInfoResponseInterface } from '../response/interfaces/bankinfo.interface';
import { OperativeBankInfoResponseModel } from '../response/models/bankinfo.model';
import {
  ContributionEnumerationInterface,
  ContributionEnumerationModel,
  EnumerationFundLocationInterface,
  EnumerationFundLocationModel,
  OperativeContributionRequestInterface,
  OperativeContributionResponse,
  OperativeContributionResponseInterface,
} from '..';
import { OperativeWithdrawalRequestInterface } from '../request/interfaces/withdrawal.interface';
import { OperativeResendSmsResponseModel } from '../response/models/resendSms.response.model';
import { OperativeResendSmsResponseInterface } from '../response/interfaces/resendSms.response.interface';

@Injectable({
  providedIn: 'root',
})
export class OperativeService {
  constructor(
    @Inject(TECH_LIB_PORTAL_CONFIG) private config: TechLibPortalConfig,
    private httpClient: HttpClient,
    private serializer: Serializer,
    private session: SessionService
  ) {}

  private updateSession() {
    this.config.sessionToken = this.session.sessionToken;
    this.config.clientCode = this.session.clientCode;
  }

  getTypeOfContribution(
    typeOfContribution: string
  ): Observable<ContributionEnumerationInterface[]> {
    const url =
      this.config.baseApi +
      Utils.formatString(OperativeEnum.api_operative_get_type_contribution, [
        typeOfContribution,
      ]);

    return new Observable(
      (observer: Observer<ContributionEnumerationModel[]>) => {
        this.httpClient
          .get(url)
          .pipe(
            map((resp: any) => {
              const t = this.serializer.fromJsonArray(
                resp,
                ContributionEnumerationModel
              );
              return t;
            })
          )
          .subscribe({
            next: (resp: any) => {
              observer.next(resp);
              observer.complete();
            },
            error: (err) => observer.error(err),
          });
      }
    );
  }

  getFundLocation(): Observable<EnumerationFundLocationInterface[]> {
    const url =
      this.config.baseApi + OperativeEnum.api_operative_get_fund_location;

    return new Observable(
      (observer: Observer<EnumerationFundLocationModel[]>) => {
        this.httpClient
          .get(url)
          .pipe(
            map((resp: any) => {
              const t = this.serializer.fromJsonArray(
                resp,
                EnumerationFundLocationModel
              );
              return t;
            })
          )
          .subscribe({
            next: (resp: any) => {
              observer.next(resp);
              observer.complete();
            },
            error: (err) => observer.error(err),
          });
      }
    );
  }

  getWithdrawalEnumeration(): Observable<ContributionEnumerationInterface[]> {
    const url =
      this.config.baseApi + OperativeEnum.api_operative_get_type_withdrawal;

    return new Observable(
      (observer: Observer<ContributionEnumerationModel[]>) => {
        this.httpClient
          .get(url)
          .pipe(
            map((resp: any) => {
              const t = this.serializer.fromJsonArray(
                resp,
                ContributionEnumerationModel
              );
              return t;
            })
          )
          .subscribe({
            next: (resp: any) => {
              observer.next(resp);
              observer.complete();
            },
            error: (err) => observer.error(err),
          });
      }
    );
  }

  getPortfolioData(
    portfolioCode: string
  ): Observable<OperativePortfolioResponseInterface> {
    this.updateSession();

    const url =
      this.config.baseApi +
      Utils.formatString(OperativeEnum.api_operative_get_portfolio, [
        portfolioCode,
      ]);

    return new Observable(
      (observer: Observer<OperativePortfolioResponseInterface>) => {
        this.httpClient
          .get(url)
          .pipe(
            map((resp: any) => {
              // console.log(resp);
              const t = this.serializer.fromJson(
                resp,
                OperativePortfolioResponseModel
              );
              return t;
            })
          )
          .subscribe(
            (resp: any) => {
              observer.next(resp);
              observer.complete();
            },
            (err) => observer.error(err)
          );
      }
    );
  }

  getModelPortfolioData(
    modelPortfolioCode: string
  ): Observable<OperativeModelPortfolioResponseInterface> {
    //this.updateSession();

    const url =
      this.config.baseApi +
      Utils.formatString(OperativeEnum.api_operative_get_modelportfolio, [
        modelPortfolioCode,
      ]);

    return new Observable(
      (observer: Observer<OperativeModelPortfolioResponseInterface>) => {
        this.httpClient
          .get(url)
          .pipe(
            map((resp: any) => {
              // console.log(resp);
              const t = this.serializer.fromJson(
                resp,
                OperativeModelPortfolioResponseModel
              );
              return t;
            })
          )
          .subscribe(
            (resp: any) => {
              observer.next(resp);
              observer.complete();
            },
            (err) => observer.error(err)
          );
      }
    );
  }

  getBankInfo(
    serviceCode: string
  ): Observable<OperativeBankInfoResponseInterface> {
    //this.updateSession();

    const url =
      this.config.baseApi +
      Utils.formatString(OperativeEnum.api_operative_get_servicemodels, [
        serviceCode,
      ]);

    return new Observable(
      (observer: Observer<OperativeBankInfoResponseInterface>) => {
        this.httpClient
          .get(url)
          .pipe(
            map((resp: any) => {
              // console.log(resp);
              const t = this.serializer.fromJson(
                resp.Omnibus,
                OperativeBankInfoResponseModel
              );
              return t;
            })
          )
          .subscribe(
            (resp: any) => {
              observer.next(resp);
              observer.complete();
            },
            (err) => observer.error(err)
          );
      }
    );
  }

  sendContribution(
    portfolioCode: string,
    contributionData: OperativeContributionRequestInterface
  ): Observable<OperativeContributionResponseInterface> {
    //this.updateSession();

    const body = this.serializer.toJson(contributionData);

    const url =
      this.config.baseApi +
      Utils.formatString(OperativeEnum.api_operative_sendcontribution, [
        portfolioCode,
      ]);

    return new Observable(
      (observer: Observer<OperativeContributionResponseInterface>) => {
        this.httpClient
          .post(url, body)
          .pipe(
            map((resp: any) => {
              const t = this.serializer.fromJson(
                resp,
                OperativeContributionResponse
              );
              return t;
            })
          )
          .subscribe(
            (resp: any) => {
              observer.next(resp);
              observer.complete();
            },
            (err) => observer.error(err)
          );
      }
    );
  }

  smsContributionValidation(
    portfolioCode: string,
    verificationId: string,
    code
  ): Observable<OperativeContributionResponseInterface> {
    //this.updateSession();

    this.config.smsCode = code;

    const url =
      this.config.baseApi +
      Utils.formatString(
        OperativeEnum.api_operative_sms_validation_contribution,
        [portfolioCode, verificationId]
      );

    return new Observable(
      (observer: Observer<OperativeContributionResponseInterface>) => {
        this.httpClient
          .get(url)
          .pipe(
            map((resp: any) => {
              const t = this.serializer.fromJson(
                resp,
                OperativeContributionResponse
              );
              return t;
            })
          )
          .subscribe(
            (resp: any) => {
              observer.next(resp);
              observer.complete();
            },
            (err) => observer.error(err)
          );
      }
    );
  }

  sendWithdrawal(
    portfolioCode: string,
    withdrawalData: OperativeWithdrawalRequestInterface
  ): Observable<OperativeContributionResponseInterface> {
    //this.updateSession();

    const body = this.serializer.toJson(withdrawalData);

    const url =
      this.config.baseApi +
      Utils.formatString(OperativeEnum.api_operative_sendwithdrawal, [
        portfolioCode,
      ]);

    return new Observable(
      (observer: Observer<OperativeContributionResponseInterface>) => {
        this.httpClient
          .post(url, body)
          .pipe(
            map((resp: any) => {
              const t = this.serializer.fromJson(
                resp,
                OperativeContributionResponse
              );
              return t;
            })
          )
          .subscribe(
            (resp: any) => {
              observer.next(resp);
              observer.complete();
            },
            (err) => observer.error(err)
          );
      }
    );
  }

  smsWithdrawalValidation(
    portfolioCode: string,
    verificationId: string,
    code
  ): Observable<OperativeContributionResponseInterface> {
    //this.updateSession();

    this.config.smsCode = code;

    const url =
      this.config.baseApi +
      Utils.formatString(
        OperativeEnum.api_operative_sms_validation_withdrawal,
        [portfolioCode, verificationId]
      );

    return new Observable(
      (observer: Observer<OperativeContributionResponseInterface>) => {
        this.httpClient
          .get(url)
          .pipe(
            map((resp: any) => {
              const t = this.serializer.fromJson(
                resp,
                OperativeContributionResponse
              );
              return t;
            })
          )
          .subscribe(
            (resp: any) => {
              observer.next(resp);
              observer.complete();
            },
            (err) => observer.error(err)
          );
      }
    );
  }

  smsResend(
    portfolioCode: string,
    verificationId: string
  ): Observable<OperativeResendSmsResponseInterface> {
    //this.updateSession();

    const url =
      this.config.baseApi +
      Utils.formatString(OperativeEnum.api_operative_sms_resend, [
        portfolioCode,
        verificationId,
      ]);

    return new Observable(
      (observer: Observer<OperativeResendSmsResponseInterface>) => {
        this.httpClient
          .get(url)
          .pipe(
            map((resp: any) => {
              const t = this.serializer.fromJson(
                resp,
                OperativeResendSmsResponseModel
              );
              return t;
            })
          )
          .subscribe(
            (resp: any) => {
              observer.next(resp);
              observer.complete();
            },
            (err) => observer.error(err)
          );
      }
    );
  }
}
